const RETREAT_FINDER_STEPS = 5;

// const RETREAT_FINDER_BENEFIT_TAGS = {
// 	'healthy-holiday': ['healthy holiday'],
// 	'self-care-and-general-wellbeing': ['self care'],
// 	'detox-and-health-cleanse': ['detox'],
// 	'emotional-healing': ['emotional healing'],
// };
//
// const RETREAT_FINDER_INTEREST_TAGS = {
// 	'open-to-anything': [],
// 	'yoga-pilates-and-mindful-living': ['yoga'],
// 	'longevity-and-cognitive-health': ['longevity'],
// 	'nutrition-and-microbiome': ['nutrition'],
// 	'stress-and-burnout': ['stress'],
// };

/**
 * Parses CSV text (RFC 4180-ish, handles quoted fields) into an array of row arrays.
 * @param {string} text - Raw CSV text.
 * @returns {string[][]} Parsed rows.
 */
function parseCsvRows(text) {
	const rows = [];
	let row = [];
	let field = '';
	let inQuotes = false;

	for (let i = 0; i < text.length; i += 1) {
		const char = text[i];
		const next = text[i + 1];

		if (inQuotes) {
			if (char === '"' && next === '"') {
				field += '"';
				i += 1;
			} else if (char === '"') {
				inQuotes = false;
			} else {
				field += char;
			}
			continue;
		}

		if (char === '"') {
			inQuotes = true;
		} else if (char === ',') {
			row.push(field);
			field = '';
		} else if (char === '\n' || char === '\r') {
			if (char === '\r' && next === '\n') i += 1;
			row.push(field);
			field = '';
			if (row.some((cell) => cell.trim() !== '')) rows.push(row);
			row = [];
		} else {
			field += char;
		}
	}

	if (field !== '' || row.length) {
		row.push(field);
		if (row.some((cell) => cell.trim() !== '')) rows.push(row);
	}

	return rows;
}

/**
 * Parses a comma-separated CSV cell into a lowercased, trimmed value list.
 * @param {string} value - Raw cell value.
 * @returns {string[]} List of values.
 */
function splitListCell(value) {
	return (value || '')
		.split(',')
		.map((item) => item.trim().toLowerCase())
		.filter(Boolean);
}

/**
 * Escapes a value for safe use as HTML text or attribute content.
 * @param {*} value - Value to escape.
 * @returns {string} Escaped string.
 */
function escapeHtml(value) {
	return String(value == null ? '' : value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

/**
 * Parses retreat data CSV text (NAME, NIGHTS, BENEFITS, SPECIAL INTERESTS,
 * CAFFEINE FREE, EXCERPT, IMAGE, LINK) into an array of retreat objects.
 * @param {string} text - Raw CSV text.
 * @returns {object[]} Retreat objects.
 */
function parseRetreatsCsv(text) {
	const rows = parseCsvRows(text);
	if (!rows.length) return [];

	const header = rows.shift().map((cell) => cell.trim().toUpperCase());

	return rows.map((row) => {
		const record = {};
		header.forEach((key, index) => {
			record[key] = (row[index] || '').trim();
		});

		return {
			name: record.NAME || '',
			nights: parseInt(record.NIGHTS, 10) || 0,
			benefits: splitListCell(record.BENEFITS),
			specialInterests: splitListCell(record['SPECIAL INTERESTS']),
			caffeineFree: (record['CAFFEINE FREE'] || '').trim().toLowerCase(),
			description: record.EXCERPT || '',
			image: record.IMAGE || '',
			url: record.LINK || '#',
			dates: [],
		};
	}).filter((retreat) => retreat.name);
}

/**
 * Parses the retreat dates CSV (no header; rows of retreat name, DD/MM/YYYY date)
 * into a map of lowercased retreat name to a list of ISO (YYYY-MM-DD) date strings.
 * @param {string} text - Raw CSV text.
 * @returns {Object<string, string[]>} Map of retreat name to available dates.
 */
function parseDatesCsv(text) {
	const rows = parseCsvRows(text);
	const datesByRetreat = {};

	rows.forEach((row) => {
		const name = (row[0] || '').trim();
		const dateStr = (row[1] || '').trim();
		if (!name || !dateStr) return;

		const parts = dateStr.split('/').map((part) => part.trim());
		if (parts.length !== 3) return;

		const [day, month, year] = parts;
		const iso = `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

		const key = name.toLowerCase();
		if (!datesByRetreat[key]) datesByRetreat[key] = [];
		datesByRetreat[key].push(iso);
	});

	return datesByRetreat;
}

function matchesNights(retreat, answers) {
	if (!answers.nights) return true;
	if (answers.nights === '7+') return retreat.nights >= 7;
	return retreat.nights === parseInt(answers.nights, 10);
}

function matchesBenefits(retreat, answers) {
	if (!answers.benefits) {
		return true;
	}
	return retreat.benefits.includes(answers.benefits);
}

function matchesSpecialInterests(retreat, answers) {
	const interest = answers['special-interests'];
	if (!interest || interest === 'all') {
		return true;
	}
	return retreat.specialInterests.includes(interest);
}

function matchesCaffeine(retreat, answers) {
	return answers.caffeine !== 'no' || retreat.caffeineFree === 'no';
}

function matchesDate(retreat, answers) {
	const range = answers['preferred-date'];
	if (answers['date-preference'] !== 'preferred' || !range) return true;

	const [start, end] = range;
	if (!start || !end) return true;

	return retreat.dates.some((date) => date >= start && date <= end);
}

/**
 * Finds retreats matching the given answers, progressively relaxing constraints
 * (date, then special interests, caffeine, benefits, nights) if there are no matches.
 * @param {object[]} retreats - Parsed retreat objects.
 * @param {object} answers - Selected quiz answers.
 * @returns {object[]} Matches and whether constraints were relaxed.
 */
function findMatchingRetreats(retreats, answers) {
	return retreats.filter((r) => {
		return matchesNights(r, answers) &&
			matchesDate(r, answers) &&
			matchesSpecialInterests(r, answers) &&
			matchesCaffeine(r, answers) &&
			matchesBenefits(r, answers);
	});
}

class RetreatFinderQuiz extends HTMLElement {
	constructor() {
		super();
		this.state = {
			stepIndex: 0,
			answers: {
				nights: null,
				'date-preference': null,
				'preferred-date': null,
				benefits: null,
				caffeine: null,
				'special-interests': null,
			},
			retreats: null,
			loading: false,
			error: null,
		};
	}

	connectedCallback() {
		this.renderStep();
		this.addEventListener('click', this.handleClick.bind(this));

		this.beforeOpenHandler = () => this.loadData();
		document.addEventListener('on:retreat-finder:before-open', this.beforeOpenHandler);
	}

	disconnectedCallback() {
		document.removeEventListener('on:retreat-finder:before-open', this.beforeOpenHandler);
		this.destroyDatePicker();
	}

	/**
	 * Opens a flatpickr calendar so the user can choose a preferred date range, jumping
	 * straight to the results once a full range is picked, skipping the remaining questions.
	 */
	openDatePicker() {
		const input = this.querySelector('.js-retreat-finder-date-input');
		if (!input || typeof flatpickr === 'undefined') {
			this.goToStep(RETREAT_FINDER_STEPS);
			return;
		}

		this.destroyDatePicker();

		const toIsoDate = (date) => {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		};

		const options = {
			mode: 'range',
			dateFormat: 'Y-m-d',
			minDate: 'today',
			onChange: (selectedDates) => {
				if (selectedDates.length < 2) return;
				this.state.answers['preferred-date'] = selectedDates.map(toIsoDate);
				this.destroyDatePicker();
				this.goToStep(RETREAT_FINDER_STEPS);
			},
		};

		this.datePicker = flatpickr(input, options);
		if (Array.isArray(this.datePicker)) {
			this.datePicker = this.datePicker[0];
		}

		if (!this.datePicker || typeof this.datePicker.open !== 'function') {
			this.datePicker = null;
			this.goToStep(RETREAT_FINDER_STEPS);
			return;
		}

		this.datePicker.open();
	}

	/**
	 * Destroys the active flatpickr instance, if any, so its calendar element
	 * doesn't linger in the document after the step re-renders.
	 */
	destroyDatePicker() {
		if (!this.datePicker) return;
		this.datePicker.destroy();
		this.datePicker = null;
	}

	/**
	 * Collects the unique set of retreat dates (ISO strings) parsed from the dates
	 * CSV for retreats matching the nights answer already selected, so the date
	 * picker only allows selecting dates that actually have a matching retreat
	 * departure.
	 * @returns {string[]} Sorted list of available ISO dates.
	 */
	getAvailableDates() {
		const dates = new Set();
		(this.state.retreats || [])
			.filter((retreat) => matchesNights(retreat, this.state.answers))
			.forEach((retreat) => {
				retreat.dates.forEach((date) => dates.add(date));
			});
		return [...dates].sort();
	}

	/**
	 * Fetches and parses the retreats and dates CSVs, if not already loaded.
	 */
	async loadData() {
		if (this.state.retreats || this.state.loading) return;

		const {csvUrl, datesCsvUrl} = this.dataset;
		if (!csvUrl) {
			this.state.error = 'The retreat finder has not been set up yet. Please add a retreat data CSV URL in the theme editor.';
			this.renderStep();
			return;
		}

		this.state.loading = true;
		this.renderStep();

		try {
			const fetchText = async (url) => {
				const response = await fetch(url);
				if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
				return response.text();
			};

			const [retreatsText, datesText] = await Promise.all([
				fetchText(csvUrl),
				datesCsvUrl ? fetchText(datesCsvUrl) : Promise.resolve(''),
			]);

			const retreats = parseRetreatsCsv(retreatsText);
			const datesByRetreat = datesText ? parseDatesCsv(datesText) : {};

			retreats.forEach((retreat) => {
				retreat.dates = datesByRetreat[retreat.name.toLowerCase()] || [];
			});

			this.state.retreats = retreats;
		} catch (error) {
			this.state.error = "Sorry, we couldn't load retreat information right now. Please try again shortly.";
		} finally {
			this.state.loading = false;
			this.renderStep();
		}
	}

	/**
	 * Handles clicks within the quiz (option selection and navigation).
	 * @param {MouseEvent} evt - Click event.
	 */
	handleClick(evt) {
		const optionBtn = evt.target.closest('.retreat-finder__option');
		if (optionBtn) {
			this.state.answers[optionBtn.dataset.category] = optionBtn.value;

			if (optionBtn.dataset.category === 'date-preference' && optionBtn.value === 'preferred') {
				this.openDatePicker();
				return;
			}

			this.goToStep(this.state.stepIndex + 1);
			return;
		}

		if (evt.target.closest('.js-retreat-finder-back')) {
			this.goToStep(this.state.stepIndex - 1);
			return;
		}

		if (evt.target.closest('.js-retreat-finder-restart')) {
			this.state.stepIndex = 0;
			this.state.answers = {
				nights: null,
				'date-preference': null,
				'preferred-date': null,
				benefits: null,
				caffeine: null,
				'special-interests': null,
			};
			this.renderStep();
		}
	}

	/**
	 * Moves to the given step index and renders it. On reaching the results step,
	 * automatically navigates to the single matching retreat if there's only one.
	 * @param {number} index - Step index to move to.
	 */
	goToStep(index) {
		this.state.stepIndex = Math.max(0, Math.min(index, RETREAT_FINDER_STEPS));
		this.renderStep();
	}

	/**
	 * Renders the current step's markup.
	 */
	renderStep() {
		this.destroyDatePicker();

		if (this.state.error) {
			this.innerHTML = `<div class="retreat-finder__error">${escapeHtml(this.state.error)}</div>`;
			return;
		}

		if (this.state.loading) {
			this.innerHTML = '<div class="retreat-finder__loading">Loading retreats…</div>';
			return;
		}

		if (!this.state.retreats || !this.state.retreats.length) {
			this.innerHTML = '<div class="retreat-finder__error">No retreat information is available right now.</div>';
			return;
		}

		switch (this.state.stepIndex) {
			case 0:
				this.innerHTML = this.renderDatePreference();
				return;
			case 1:
				this.innerHTML = this.renderNightsStep();
				return;
			case 2:
				this.innerHTML = this.renderBenefits();
				return;
			case 3:
				this.innerHTML = this.renderCaffeineOptions();
				return;
			case 4:
				this.innerHTML = this.renderSpecialInterests();
				return;
			case 5:
				this.innerHTML = this.renderResults();
				return;
		}
	}

	renderNightsStep() {
		const buckets = new Map();
		this.state.retreats.forEach((retreat) => {
			if (!retreat.nights) return;
			const value = retreat.nights < 7 ? String(retreat.nights) : '7+';
			if (!buckets.has(value)) {
				buckets.set(value, `${value} night${retreat.nights > 1 ? 's' : ''}`);
			}
		});

		const sortedValues = [...buckets.keys()].sort((a, b) => {
			const numA = a === '7+' ? 7 : parseInt(a, 10);
			const numB = b === '7+' ? 7 : parseInt(b, 10);
			return numA - numB;
		});

		const buttonsHtml = sortedValues
			.map((value) => this.createOption(buckets.get(value), value, 'nights'))
			.join('');

		return `
			${this.renderPagination()}
			<div class="retreat-finder_content">
				${this.renderStepHeading('How long would you like to stay?')}
				<div class="retreat-finder__options">${buttonsHtml}</div>
			</div>
		`;
	}

	renderDatePreference() {
		const category = 'date-preference';
		return `
			${this.renderPagination()}
			<div class="retreat-finder_content">
				${this.renderStepHeading('Do you have preferred dates?')}
				<div class="retreat-finder__options">
					${this.createOption('Yes, I have dates in mind', 'preferred', category)}
					${this.createOption('I\'m flexible, find the right retreat for me', 'flexible', category)}
				</div>
				<input type="text" class="retreat-finder__date-input js-retreat-finder-date-input visually-hidden" readonly>
			</div>
		`
	}

	renderBenefits() {
		const category = 'benefits';
		return `
			${this.renderPagination()}
			<div class="retreat-finder_content">
				${this.renderStepHeading('What benefits are you seeking?')}
				<div class="retreat-finder__options">
					${this.createOption('A healthy holiday', 'healthy holiday', category)}
					${this.createOption('Self care and general wellbeing', 'self care', category)}
					${this.createOption('Detox and health cleanse', 'detox', category)}
					${this.createOption('Emotional healing', 'emotional healing', category)}
				</div>
			</div>
		`;
	}

	renderCaffeineOptions() {
		const category = 'caffeine';
		return `
			${this.renderPagination()}
			<div class="retreat-finder_content">
				${this.renderStepHeading('Are you comfortable commiting to a caffeine-free retreat?')}
				<div class="retreat-finder__options">
					${this.createOption("Yes, I'm ready to embrace the benefits", 'yes', category)}
					${this.createOption("No, I'd still prefer my morning coffee", 'no', category)}
				</div>
			</div>
		`;
	}

	renderSpecialInterests() {
		const category = 'special-interests';
		return `
			${this.renderPagination()}
			<div class="retreat-finder_content">
				${this.renderStepHeading('Are you drawn to any special interest topics?')}
				<div class="retreat-finder__options">
					${this.createOption('Open to anything', 'all', category)}
					${this.createOption('Yoga, Pilates and Mindful Living', 'yoga', category)}
					${this.createOption('Longevity and Cognitive Health', 'longevity', category)}
					${this.createOption('Nutrition and Microbiome', 'nutrition', category)}
					${this.createOption('Stress and Burnout', 'stress', category)}
				</div>
			</div>
		`;
	}

	/**
	 * Renders the results step, showing matching retreats or a no-matches message.
	 * @returns {string} Step HTML.
	 */
	renderResults() {
		const retreats= findMatchingRetreats(this.state.retreats, this.state.answers);
		const {fallbackUrl} = this.dataset;
		const isDateSearch = this.state.answers['date-preference'] === 'preferred' && this.state.answers['preferred-date'];

		if (!retreats.length) {
			const message = isDateSearch
				? 'Sorry, no retreats are available on your selected dates.'
				: "Try different answers, or browse all of our retreats to discover the one that's right for you.";

			const phone = '(07) 5589 5000';
			const phoneLink = 'tel:0755895000';

			return `
				${this.renderPagination()}
				<div class="retreat-finder_content">
					<h3 class="retreat-finder__step-title">We couldn't find a matching retreat</h3>
					<p>${message}</p>
					<div class="retreat-finder__actions">
						<button type="button" class="btn btn--secondary js-retreat-finder-restart">Start over</button>
						${fallbackUrl ? `<a class="btn btn--primary" href="${escapeHtml(fallbackUrl)}">View all retreats</a>` : ''}
					</div>
					<h4>Or</h4>
					<div class="retreat-finder__contact-horizontal-card">
						<a href="${phoneLink}" class="horizontal-card-icon">
							<svg class="icon feather feather-phone" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#232b26" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-phone">
								<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
							</svg>
						</a>
						<div>
							<p><strong>Speak with a Retreat Advisor</strong></p>
							<p>
								We're here to help you find the right retreat.
								<br>
								Call us on <a href="${phoneLink}">${phone}</a>
							</p>
						</div>
					</div>
				</div>
			`;
		}

		const resultsHtml = retreats.map((retreat) => `
			<div class="retreat-finder__result">
				${retreat.image ? `<img class="retreat-finder__result-image" src="${escapeHtml(retreat.image)}" alt="" loading="lazy">` : ''}
				<div>${retreat.nights} nights</div>
				<h3><a href="${escapeHtml(retreat.url)}">${escapeHtml(retreat.name)}</a></h3>
				<div>${retreat.description}</div>
				<div class="retreat-finder__buttons">
					<a href="${escapeHtml(retreat.url)}" class="btn btn--light-outline">View Retreat</a>
					<a href="https://booking.gwinganna.com/" class="btn btn--secondary">Book Now</a>
				</div>
			</div>
		`).join('');

		return `
			${this.renderPagination()}
			<div class="retreat-finder_content">
				<h3 class="retreat-finder__step-title">Your best match</h3>
				<div class="retreat-finder__results">${resultsHtml}</div>
			</div>
		`;
	}

	createOption(name, value, category) {
		const selected = this.state.answers[category] === value ? 'selected' : '';
		return `
			<button type="button" class="retreat-finder__option ${selected}" value="${value}" data-category="${category}">
				${name}
			</button>
		`;
	}

	renderPagination() {
		const pagination = [];
		for (let i = 0; i < RETREAT_FINDER_STEPS; i++) {
			pagination.push(`<li class="${i <= this.state.stepIndex ? 'complete' : ''}"></li>`);
		}
		const arrow = `
			<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#FFF" class="bi bi-arrow-left" viewBox="0 0 16 16">
				<path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/>
			</svg>
		`;
		const back = this.state.stepIndex < RETREAT_FINDER_STEPS ? `
			<button type="button" class="link retreat-finder__back js-retreat-finder-back">
				${arrow}
				Back
			</button>` : `
			<button type="button" class="link retreat-finder__back js-retreat-finder-restart">
				${arrow}
				Start over
			</button>
			`;
		return `
			<ul class="retreat-finder__pagination">
				${pagination.join('')}
			</ul>
			${ this.state.stepIndex > 0 ? back : ''}
		`;
	}

	renderStepHeading(title) {
		return `
			<div class="retreat-finder__progress">Question ${this.state.stepIndex + 1} of ${RETREAT_FINDER_STEPS}</div>
			<h3 class="retreat-finder__step-title">${title}</h3>
		`;
	}
}

customElements.define('retreat-finder-quiz', RetreatFinderQuiz);
